import { useEffect, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { CommodityCode } from "../../../../../../../models/commodity.code";
import { Error } from "../../../../../../../models/error";
import { Filters, Searches, Sort, Sorts } from "../../../../../../../models/shipeezi.table";
import CommodityCodeService from "../../../../../../../services/commodity.code.service";
import { checkError } from "../../../../../../../util/checkError";
import { CommodityCodeKeys } from "../../../../../../../util/queries";
import { CodeType, CodeTypeNumber, removeAllCharactersAndSymbols } from "../utils";

const CACHE_TIME = 60 * 60 * 1000; // 1 hour
const STALE_TIME = 2 * 60 * 1000; // 2 minutes

const useActions = () => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const queryClient = useQueryClient();

  const [commodityCodes, setCommodityCodes] = useState<CommodityCode[]>([]);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();

  const {
    data,
    refetch: refetchCommodityCodes,
    isFetching,
  } = useQuery({
    queryKey: CommodityCodeKeys.getAndCountAll([page, pageSize, filters, searches, sorting]),
    queryFn: async () => {
      const response = await CommodityCodeService.getAndCountAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        sortBy: sorting ? JSON.stringify(sorting) : JSON.stringify([["section", "ASC"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      });

      if (response && response.data) return response.data;
    },
    enabled: !!currentUser?.companyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: CACHE_TIME,
  });

  const {
    data: hscodes,
    refetch: refetchHSCodes,
    isFetching: isFetchingHSCodes,
  } = useQuery({
    cacheTime: CACHE_TIME,
    enabled: !!currentUser?.companyId,
    queryFn: async () => {
      const response = await CommodityCodeService.getAll({
        attributes: JSON.stringify([
          "chapterId",
          "description",
          "hscode",
          "sectionId",
        ]) as unknown as string[],
        companyId: currentUser?.companyId,
        level: CodeTypeNumber[CodeType.HS_CODE],
        sortBy: JSON.stringify([["hscode"]]),
      });
      const hscodes = response?.data || [];

      return hscodes;
    },
    queryKey: CommodityCodeKeys.getAll([
      {
        companyId: currentUser?.companyId,
        level: CodeTypeNumber[CodeType.HS_CODE],
      },
    ]),
    staleTime: STALE_TIME,
  });

  const refetch = () => {
    refetchCommodityCodes();
    refetchHSCodes();
  };

  const { data: sections, isFetching: fetchingSections } = useQuery({
    queryKey: CommodityCodeKeys.getSections(),
    queryFn: async () => {
      const response = await CommodityCodeService.getSections();

      if (response && response.data) return response.data;

      return [];
    },
    enabled: !!currentUser?.companyId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: CACHE_TIME,
  });

  const { data: regions, isFetching: fetchingRegions } = useQuery({
    queryKey: CommodityCodeKeys.getRegions(),
    queryFn: async () => {
      const response = await CommodityCodeService.getRegions();

      if (response && response.data) return response.data;

      return [];
    },
    enabled: !!currentUser?.companyId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: CACHE_TIME,
  });

  const useCreateCommodityCode = () => {
    return useMutation({
      mutationKey: CommodityCodeKeys.create(),
      mutationFn: (commodityCode: CommodityCode) => CommodityCodeService.create(commodityCode),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CommodityCodeKeys.all], refetchType: "none" });
        refetchCommodityCodes();
        NotificationToast.success(translate("operationPerformedSuccessfully"));
      },
      onError: (error) => {
        const errorAxios = error as AxiosError<Error>;

        if (errorAxios?.response?.data && errorAxios?.response?.data.field !== "unknown") {
          return;
        }
        if (errorAxios?.response?.data && typeof errorAxios?.response?.data === "object") {
          checkError(errorAxios, translate("anErrorOccurredTryAgain"));
        }
      },
    });
  };

  const useCreateCommodityCodes = () => {
    return useMutation({
      mutationKey: CommodityCodeKeys.createList(),
      mutationFn: (commodityCodes: CommodityCode[]) =>
        CommodityCodeService.createList(commodityCodes),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CommodityCodeKeys.all], refetchType: "none" });
        refetchCommodityCodes();
        NotificationToast.success(translate("operationPerformedSuccessfully"));
      },
      onError: (error) => {
        const errorAxios = error as AxiosError<Error>;

        if (errorAxios?.response?.data && errorAxios?.response?.data.field !== "unknown") {
          return;
        }
        if (errorAxios?.response?.data && typeof errorAxios?.response?.data === "object") {
          checkError(errorAxios, translate("anErrorOccurredTryAgain"));
        }
      },
    });
  };

  const useUpdateCommodityCode = () => {
    return useMutation({
      mutationKey: CommodityCodeKeys.update(),
      mutationFn: ({ id, commodityCode }: { id: number; commodityCode: CommodityCode }) =>
        CommodityCodeService.update(id, commodityCode),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CommodityCodeKeys.all], refetchType: "none" });
        refetchCommodityCodes();
        NotificationToast.success(translate("operationPerformedSuccessfully"));
      },
      onError: (error) => {
        const errorAxios = error as AxiosError<Error>;

        if (errorAxios?.response?.data && errorAxios?.response?.data.field !== "unknown") {
          return;
        }
        if (errorAxios?.response?.data && typeof errorAxios?.response?.data === "object") {
          checkError(errorAxios, translate("anErrorOccurredTryAgain"));
        }
      },
    });
  };

  const useDeleteCommodityCode = () => {
    return useMutation({
      mutationKey: CommodityCodeKeys.delete(),
      mutationFn: (id: number) => CommodityCodeService.delete(id),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CommodityCodeKeys.all], refetchType: "none" });
        refetchCommodityCodes();
        NotificationToast.success(translate("deletedSuccessfully"));
      },
    });
  };

  const useSwitchStatusCommodityCode = () => {
    return useMutation({
      mutationKey: CommodityCodeKeys.switchStatus(),
      mutationFn: (id: number) =>
        CommodityCodeService.switchStatus(id, currentUser?.companyId || 0),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CommodityCodeKeys.all], refetchType: "none" });
        refetch();
        NotificationToast.success(translate("operationPerformedSuccessfully"));
      },
    });
  };

  useEffect(() => {
    if (data && data?.rows) {
      setCommodityCodes(data.rows);
      setTotal(data.count);
    }
  }, [data]);

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    // Remove non digit characters from hscode when searching
    if (value && value?.length && value[0].hscode) {
      value[0].hscode = removeAllCharactersAndSymbols(value[0].hscode);
    }

    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    variables: {
      commodityCodes,
      sections,
      regions,
      page,
      pageSize,
      total,
      isFetching,
      isFetchingHSCodes,
      fetchingSections,
      fetchingRegions,
      hscodes,
    },
    refetch,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    setPageSize,
    useCreateCommodityCode,
    useCreateCommodityCodes,
    useUpdateCommodityCode,
    useDeleteCommodityCode,
    useSwitchStatusCommodityCode,
  };
};

export default useActions;
